"use client"

import { CheckCircle2, AlertCircle, Loader2, Clock, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { resolveToolLabel } from "@/lib/ai-chat/tool-display"
import { useTranslation } from '@/lib/i18n'

interface ToolResultCardProps {
  toolName: string
  /** Server-authored human label for `toolName`, when the backend sent one. */
  toolLabel?: string
  status: "executing" | "Completed" | "error" | "confirming"
  success?: boolean
  summary?: string
}

// A 45-entry hand-maintained map used to live here, covering 45 of 998 real
// AI-chat tools — everything else fell through to
// `toolName.replace(/_/g, " ")`, a prettified-but-still-underscored
// identifier. That is a drift source (the map goes stale the moment a new
// tool ships) as well as an incomplete fix. resolveToolLabel replaces it:
// it prefers the server-authored `toolLabel` (the manifest-driven successor
// to this map — see lib/ai-chat/tool-display.ts) and otherwise humanizes
// the raw id so it is always readable, even for a tool nobody has labelled
// yet.
export function ToolResultCard({ toolName, toolLabel, status, success, summary }: ToolResultCardProps) {
  const { t } = useTranslation()
  const displayName = resolveToolLabel(toolName, toolLabel, t)

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 py-1 text-xs min-w-0",
        status === "executing" && "text-muted-foreground",
        status === "Completed" && success && "text-muted-foreground",
        status === "Completed" && !success && "text-destructive/70",
        status === "error" && "text-destructive/70",
        status === "confirming" && "text-yellow-600 dark:text-yellow-400"
      )}
    >
      {status === "executing" && <Loader2 className="h-3 w-3 animate-spin shrink-0" />}
      {status === "Completed" && success && (
        <CheckCircle2 className="h-3 w-3 text-primary/60 shrink-0" />
      )}
      {(status === "error" || (status === "Completed" && !success)) && (
        <AlertCircle className="h-3 w-3 shrink-0" />
      )}
      {status === "confirming" && <Clock className="h-3 w-3 shrink-0" />}

      {/* No present/past-tense rewrite here (there used to be one): it
          regex-matched any trailing "ing" word, which read fine for the old
          map's hand-picked gerunds ("Searching" -> "Searched") but silently
          mangled real tool vocabulary on this platform — "Phishing campaign
          start" would have become "Phished campaign start" the moment a
          phishing_* tool completed. The icon + color already distinguish
          in-progress from done, so the label stays identical in both
          states. */}
      <span className={cn(status === "executing" && "animate-pulse")}>{displayName}</span>
      {summary && (
        <>
          <ChevronRight className="h-2.5 w-2.5 shrink-0 opacity-40 rtl-flip" />
          <span className="truncate opacity-70">{summary}</span>
        </>
      )}
    </div>
  )
}
